// Wrist Joint Template Configuration
// Questions simplified to 12-year-old reading level

export const wristTemplate = {
  id: 'WRIST',
  name: 'Wrist',
  icon: '✋',
  dominantSide: 'hand',

  fields: {
    dominantHand: {
      type: 'radio',
      label: 'Which is your dominant hand (the one you write with)?',
      options: ['Right', 'Left'],
      required: true
    },

    injuredSide: {
      type: 'radio',
      label: 'Which wrist has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your wrist problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain',
        'Swelling',
        'Stiffness',
        'Weakness',
        'Numbness or tingling in fingers',
        'Clicking or catching',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your wrist problem started',
      rows: 3,
      required: true
    },

    hadTrauma: {
      type: 'radio',
      label: 'Did you injure your wrist (fall, twist, hit it)?',
      options: ['Yes', 'No']
    },

    traumaDescription: {
      type: 'text',
      label: 'What happened?',
      placeholder: 'e.g., fell on outstretched hand',
      showWhen: { field: 'hadTrauma', equals: 'Yes' }
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly is your pain?',
      options: [
        'Back of wrist (top)',
        'Palm side of wrist',
        'Thumb side',
        'Little finger side',
        'All around the wrist'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Aching', 'Sharp', 'Burning', 'Stabbing', 'Throbbing', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain spread anywhere else?',
      options: ['Yes, to my hand', 'Yes, to my forearm', 'Yes, to my elbow', 'No, just my wrist']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'When using my hand',
        'At night',
        'In the morning',
        'After activity'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Gripping things',
        'Twisting (like opening a jar)',
        'Writing',
        'Typing',
        'Lifting',
        'Turning palm up/down',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Wearing a splint/brace',
        'Ice',
        'Heat',
        'Pain medication',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling Section
    hasSwelling: {
      type: 'radio',
      label: 'Is your wrist swollen?',
      options: ['Yes', 'No']
    },

    swellingLocation: {
      type: 'text',
      label: 'Where is the swelling?',
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingChange: {
      type: 'radio',
      label: 'Is the swelling:',
      options: ['Getting worse', 'Getting better', 'Staying the same'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingWithActivity: {
      type: 'radio',
      label: 'Does the swelling get worse with activity?',
      options: ['Yes', 'No'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Is your wrist stiff?',
      options: ['Yes', 'No']
    },

    stiffnessMovements: {
      type: 'checkbox-multiple',
      label: 'Which movements are stiff or limited?',
      options: [
        'Bending wrist forward',
        'Bending wrist back',
        'Moving wrist towards thumb side',
        'Moving wrist towards little finger side'
      ],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    // Weakness & Instability
    hasWeakness: {
      type: 'radio',
      label: 'Does your wrist or hand feel weak?',
      options: ['Yes', 'No']
    },

    gripStrength: {
      type: 'radio',
      label: 'Can you grip things firmly?',
      options: ['Yes', 'No, my grip is weak', 'I drop things'],
      showWhen: { field: 'hasWeakness', equals: 'Yes' }
    },

    givingWay: {
      type: 'radio',
      label: 'Does your wrist feel unstable or give way?',
      options: ['Yes', 'No']
    },

    // Nerve Symptoms
    hasNumbness: {
      type: 'radio',
      label: 'Do you have numbness or tingling in your fingers?',
      options: ['Yes', 'No']
    },

    numbnessLocation: {
      type: 'checkbox-multiple',
      label: 'Which fingers feel numb or tingly?',
      options: [
        'Thumb, index, and middle fingers',
        'Ring finger and little finger',
        'All fingers'
      ],
      showWhen: { field: 'hasNumbness', equals: 'Yes' }
    },

    numbnessTiming: {
      type: 'checkbox-multiple',
      label: 'When do you get the numbness/tingling?',
      options: [
        'At night',
        'During the day',
        'When holding things in certain positions',
        'All the time'
      ],
      showWhen: { field: 'hasNumbness', equals: 'Yes' }
    },

    droppingThings: {
      type: 'radio',
      label: 'Do you drop things or have trouble with buttons?',
      options: ['Yes', 'No']
    },

    // Functional Limitations
    writingDifficulty: {
      type: 'radio',
      label: 'Can you write normally?',
      options: ['Yes', 'Yes but it hurts', 'No']
    },

    typingDifficulty: {
      type: 'radio',
      label: 'Can you type/use a computer?',
      options: ['Yes', 'Yes but it hurts', 'No']
    },

    toolsDifficulty: {
      type: 'radio',
      label: 'Can you use tools or utensils (like a knife or screwdriver)?',
      options: ['Yes', 'With difficulty', 'No']
    },

    dressingDifficulty: {
      type: 'radio',
      label: 'Can you do buttons and zips?',
      options: ['Yes', 'With difficulty', 'No']
    },

    liftingDifficulty: {
      type: 'radio',
      label: 'Can you lift things?',
      options: ['Yes', 'Light things only', 'No']
    },

    sportDifficulty: {
      type: 'radio',
      label: 'Can you do your sports or hobbies?',
      options: ['Yes', 'Yes but it hurts', 'No']
    },

    // Previous History
    previousWristProblems: {
      type: 'radio',
      label: 'Have you had wrist problems before?',
      options: ['Yes', 'No']
    },

    previousWristDetails: {
      type: 'textarea',
      label: 'Tell us about your previous wrist problems:',
      rows: 2,
      showWhen: { field: 'previousWristProblems', equals: 'Yes' }
    },

    previousWristSurgery: {
      type: 'radio',
      label: 'Have you had wrist surgery before?',
      options: ['Yes', 'No']
    },

    previousFracture: {
      type: 'radio',
      label: 'Have you ever broken your wrist?',
      options: ['Yes', 'No']
    },

    repetitiveUse: {
      type: 'radio',
      label: 'Does your work or hobby involve a lot of repetitive hand movements?',
      options: ['Yes', 'No']
    },

    repetitiveDetails: {
      type: 'text',
      label: 'What kind of repetitive movements?',
      placeholder: 'e.g., typing, using tools, knitting',
      showWhen: { field: 'repetitiveUse', equals: 'Yes' }
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Rheumatoid arthritis',
        'Osteoarthritis',
        'Gout',
        'Diabetes',
        'Thyroid problems',
        'Carpal tunnel syndrome (diagnosed before)',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    workHandUse: {
      type: 'radio',
      label: 'Does your job involve a lot of hand/wrist use?',
      options: ['Yes', 'No', 'Some']
    },

    sport: {
      type: 'text',
      label: 'What sports or hobbies do you do?',
      placeholder: 'e.g., tennis, golf, weightlifting, knitting, gaming'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    },

    coldFingers: {
      type: 'radio',
      label: 'Do your fingers change colour in the cold (go white or blue)?',
      options: ['Yes', 'No']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Wrist Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['dominantHand', 'injuredSide', 'problemDate', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription', 'hadTrauma', 'traumaDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '4. Swelling',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingLocation', 'swellingChange', 'swellingWithActivity']
    },
    {
      id: 'stiffness',
      title: '5. Stiffness & Movement',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessMovements', 'hasWeakness', 'gripStrength', 'givingWay']
    },
    {
      id: 'nerves',
      title: '6. Numbness & Tingling',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasNumbness', 'numbnessLocation', 'numbnessTiming', 'droppingThings']
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['writingDifficulty', 'typingDifficulty', 'toolsDifficulty', 'dressingDifficulty', 'liftingDifficulty', 'sportDifficulty']
    },
    {
      id: 'history',
      title: '8. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousWristProblems', 'previousWristDetails', 'previousWristSurgery', 'previousFracture', 'repetitiveUse', 'repetitiveDetails']
    },
    {
      id: 'medical',
      title: '9. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Carpal tunnel syndrome',
      'De Quervain\'s tenosynovitis',
      'TFCC tear',
      'Scaphoid fracture',
      'Wrist sprain',
      'Wrist osteoarthritis',
      'Ganglion cyst',
      'Ulnar impaction syndrome',
      'Kienbock\'s disease'
    ]
  }
};

export default wristTemplate;
